import React from 'react';
import { Skeleton } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { DefaultPageLayout } from 'layouts/default-page-layout';
import { useGetDistributionEfficiencyAggregatedStatistics } from 'api/statistic/use-get-distribution-efficiency-aggregated-statistics';
import { useGetDistributionEfficiencyStatistics } from 'api/statistic/use-get-distribution-efficiency-statistics';
import { UserRole, withRoleGuard } from 'shared/hocs/with-role-guard';
import { useDistributionEfficiencyFiltersStore } from 'shared/stores/use-distribution-efficiency-filters';
import { DistributionCost } from 'components/distribution-efficiency/distribution-cost';
import { DistributionEfficiencyFilters } from 'components/distribution-efficiency/distribution-efficiency';
import { DistributionEfficiencyAggregatedStatistic } from 'components/distribution-efficiency/distribution-efficiency-aggregated-statistic';
import { LinearChart } from 'components/insights/linear-chart';
import { ELineStyle } from 'components/insights/linear-chart/types';

export const DistributionEfficiencyPageComponent: React.FC = () => {
  const { filters } = useDistributionEfficiencyFiltersStore();

  const { data: aggregatedStatisticsData, isLoading: isAggregatedStatisticsLoading } =
    useGetDistributionEfficiencyAggregatedStatistics({ filters });

  const { data: statisticsData, isLoading: isStatisticsLoading } = useGetDistributionEfficiencyStatistics({
    filters,
  });

  const isLoading = isAggregatedStatisticsLoading || isStatisticsLoading;

  const mainSeries = [
    {
      name: 'Time until arrival',
      data: statisticsData?.series?.timeUntilArrival ?? [],
      unit: 'hours',
    },
    {
      name: 'Transit arrival time',
      data: statisticsData?.series?.transitTime ?? [],
      relatedToOppositeYAxis: true,
      unit: 'hours',
    },
  ];

  const comparisonSeries = statisticsData?.comparisonSeries
    ? [
        {
          name: 'Time until arrival',
          data: statisticsData.comparisonSeries.timeUntilArrival ?? [],
          unit: 'hours',
        },
        {
          name: 'Transit arrival time',
          data: statisticsData.comparisonSeries.transitTime ?? [],
          relatedToOppositeYAxis: true,
          unit: 'hours',
        },
      ]
    : undefined;

  const header = <PageHeader title={<DistributionEfficiencyFilters />} />;

  return (
    <DefaultPageLayout header={header}>
      <Skeleton loading={isLoading}>
        {aggregatedStatisticsData && (
          <DistributionEfficiencyAggregatedStatistic
            value={aggregatedStatisticsData.value}
            comparisonValue={aggregatedStatisticsData?.comparisonValue}
          />
        )}
        <LinearChart
          title='Average time by day'
          mainSeries={mainSeries}
          comparisonSeries={comparisonSeries}
          comparisonLineStyle={ELineStyle.Dash}
          withOppositeYAxis
          highlightWeekends
        />
      </Skeleton>
      <DistributionCost />
    </DefaultPageLayout>
  );
};

export const DistributionEfficiencyPage = withRoleGuard(DistributionEfficiencyPageComponent, [
  UserRole.admin,
  UserRole.transport_manager,
]);
